import { useQuery } from '@tanstack/react-query';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { GetAccountBalances } from 'axiosApi/models/get-account-balances';
import { queryKeys } from '../queryRegistry';
import { AccountBalance, AccountSummary } from 'types/account';
import { useContext } from 'react';
import { AuthContext } from 'components/authentication/context/authContext';
import { useAccountTypes } from './useAccountManagement';
import intl from 'components/i18n/ReactIntlWrapper';

/**
 * Hook to fetch account balance using batch endpoint (even for single accounts)
 */
export const useAccountBalance = (accountId: number, enabled = true) => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useQuery({
    queryKey: queryKeys.accountBalance(accountId),
    queryFn: async (): Promise<AccountBalance> => {
      // Use batch endpoint even for single account (more consistent)
      const request: GetAccountBalances = {
        accountIds: [accountId],
        asOfDate: undefined // Use current date
      };
      const response = await invoiceApi.apiVversionLedgerAccountsBalancesPost('1', request);
      
      const balances = response.data.data || [];
      if (balances.length === 0) {
        throw new Error(`Balance not found for account ${accountId}`);
      }

      const data = balances[0];
      
      return {
        accountId: data.accountId,
        accountNumber: data.accountNumber,
        accountName: data.accountName,
        balance: data.balance / 100,
        totalIncoming: data.totalIncoming / 100,
        totalOutgoing: data.totalOutgoing / 100,
        currency: data.currency?.code || 'USD',
        asOfDate: data.asOfDate ? new Date(data.asOfDate).toISOString() : undefined,
        entryCount: data.entryCount,
      };
    },
    enabled: enabled && !!accountId && !!userEntityId,
    staleTime: 300000, // 5 minutes
  });
};

/**
 * Hook to fetch balances for multiple accounts using batch endpoint
 */
export const useAccountBalances = (accountIds: number[], enabled = true) => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useQuery({
    queryKey: ['accountBalances', accountIds],
    queryFn: async (): Promise<AccountBalance[]> => {
      if (!accountIds || accountIds.length === 0) return [];

      // Use batch endpoint for all requests (single or multiple accounts)
      const request: GetAccountBalances = {
        accountIds: accountIds,
        asOfDate: undefined // Use current date
      };
      const response = await invoiceApi.apiVversionLedgerAccountsBalancesPost('1', request);

      const balances = response.data.data || [];
      
      return balances.map((data: any) => ({
        accountId: data.accountId,
        accountNumber: data.accountNumber,
        accountName: data.accountName,
        balance: data.balance / 100,
        totalIncoming: data.totalIncoming / 100,
        totalOutgoing: data.totalOutgoing / 100,
        currency: data.currency?.code || 'USD',
        asOfDate: data.asOfDate ? new Date(data.asOfDate).toISOString() : undefined,
        entryCount: data.entryCount,
      }));
    },
    enabled: enabled && accountIds.length > 0 && !!userEntityId,
    staleTime: 300000, // 5 minutes
  });
};

/**
 * Hook to fetch account summary grouped by category using generated APIs
 */
export const useAccountsSummary = (enabled = true) => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  // Get account types data
  const { data: accountTypes, isLoading: isLoadingAccountTypes } = useAccountTypes();

  return useQuery({
    queryKey: queryKeys.accountsSummary,
    queryFn: async (): Promise<AccountSummary[]> => {
      
      // First, fetch all accounts using generated API
      const accountsResponse = await invoiceApi.apiVversionAccountSearchGet('1', 1, 1000, '', 'name', 'asc');
      const accounts = accountsResponse.data.data?.queryResult || [];
      
      if (accounts.length === 0) return [];

      // Create lookup for account types
      const accountTypesMap = new Map();
      if (accountTypes) {
        accountTypes.forEach(type => {
          accountTypesMap.set(type.id, type);
        });
      }

      // Fetch balances for all accounts using batch endpoint
      const accountIds = accounts.map((acc: any) => acc.id);
      
      // Use single batch call instead of multiple individual calls
      const request: GetAccountBalances = {
        accountIds: accountIds,
        asOfDate: undefined // Use current date
      };
      const balancesResponse = await invoiceApi.apiVversionLedgerAccountsBalancesPost('1', request);

      const balances = balancesResponse.data.data || [];
      const balancesMap = new Map<number, AccountBalance>();
      
      balances.forEach((data: any) => {
        const balance = {
          accountId: data.accountId,
          accountNumber: data.accountNumber,
          accountName: data.accountName,
          balance: data.balance / 100,
          totalIncoming: data.totalIncoming / 100,
          totalOutgoing: data.totalOutgoing / 100,
          currency: data.currency?.code || 'USD',
          asOfDate: data.asOfDate ? new Date(data.asOfDate).toISOString() : undefined,
          entryCount: data.entryCount,
        };
        balancesMap.set(balance.accountId, balance);
      });

      // Group by category and calculate totals
      const summaryMap = new Map<string, AccountSummary>();

      accounts.forEach((account: any) => {
        // Get actual category from account type data
        const accountType = accountTypesMap.get(account.accountTypeId);
        const categoryKey = accountType?.category?.labelKey || 'accountCategory.other';
        const category = intl.get(categoryKey);
        
        const balance = balancesMap.get(account.id);

        if (!summaryMap.has(category)) {
          summaryMap.set(category, {
            category,
            totalBalance: 0,
            currency: account.currency || 'USD',
            accountCount: 0,
            accounts: [],
          });
        }

        const summary = summaryMap.get(category)!;
        summary.accountCount++;
        
        if (balance) {
          summary.totalBalance += balance.balance;
          summary.accounts.push(balance);
        }
      });

      return Array.from(summaryMap.values());
    },
    enabled: enabled && !!userEntityId && !isLoadingAccountTypes,
    staleTime: 300000, // 5 minutes
  });
};

/**
 * Hook to fetch total balance across all accounts using generated APIs
 */
export const useTotalBalance = (currency = 'USD', enabled = true) => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useQuery({
    queryKey: ['totalBalance', currency],
    queryFn: async (): Promise<number> => {
      
      // Fetch all active accounts with specified currency using generated API
      // TODO: Add currency filter when available in generated API
      const accountsResponse = await invoiceApi.apiVversionAccountSearchGet('1', 1, 1000, '', 'name', 'asc');
      const allAccounts = accountsResponse.data.data?.queryResult || [];
      
      // Filter by currency and active status on frontend for now
      const accounts = allAccounts.filter((acc: any) => 
        acc.active && acc.currency?.code === currency
      );
      
      if (accounts.length === 0) return 0;

      // Fetch all balances using generated Ledger API endpoints
      const accountIds = accounts.map((acc: any) => acc.id);
      
      const balancePromises = accountIds.map((id: number) =>
        invoiceApi.apiVversionLedgerAccountAccountIdBalanceGet(id, '1')
          .then((response: any) => (response.data.data?.balance || 0) / 100)
          .catch(() => 0)
      );

      const balances = await Promise.all(balancePromises);
      return balances.reduce((sum, balance) => sum + balance, 0);
    },
    enabled: enabled && !!userEntityId,
    staleTime: 300000, // 5 minutes
  });
};
